import * as React from "react";
import { Api } from "@deliveryhero/portal-api-client";
import FWFMain from "@deliveryhero/fwf-sdk-javascript";
import type { Hub } from "@sentry/types";

export enum RestaurantSelectorOptions {
	NONE = "NONE",
	SINGLE = "SINGLE",
	MULTIPLE = "MULTIPLE",
	SINGLE_VENDOR = "SINGLE_VENDOR",
	MULTIPLE_VENDORS = "MULTIPLE_VENDORS",
}

export enum Subject {
	USER = "1",
	IMPERSONATOR = "impersonator",
}

export interface IPlatform {
	name: string;
	nameReadable: string;
}

export interface IPluginConfig {
	restaurantSelector?: RestaurantSelectorOptions;
}

export interface IRestaurantData {
	id: number;
	name: string;
	timezone: string;
	currency: IRestaurantCurrency;
	address: IRestaurantAddress;
	platforms: IRestaurantPlatform[];
}

export interface TransmissionMethod {
	type: string;
	is_primary: true;
	global_transmission_method_id: string;
}

export interface Transmission {
	methods: TransmissionMethod[];
}

export interface IVendorData {
	name: string;
	id: string;
	vendorId: string;
	globalVendorId: string;
	globalEntityId: string;
	chainId: string;
	chainName: string;
	billingParentId: string;
	creationDate: string;
	businessType: string;
	verticalType: string;
	keyAccount: boolean;
	deliveryTypes: string[];
	joker: IJoker;
	timezone: string;
	currency: IRestaurantCurrency;
	address: IRestaurantAddress;
	isOpen?: boolean;
	isOnline?: boolean;
	accountType: string;
	isConcept: boolean;
	transmission: Transmission;
}

export interface IGroup {
	[key: string]: string;
}

export interface IRestaurantCurrency {
	code: string;
	symbol: string;
}

export interface IRestaurantPlatform {
	code?: string;
	externalRestaurantId: string;
	externalRestaurantName?: string;
	name?: string;
	platformId?: number;
	platformKey: string;
}

export interface IRestaurantCurrency {
	code: string;
	symbol: string;
}

export interface IRestaurantCountry {
	name: string;
	code: string;
}

export interface IRestaurantAddress {
	street?: string;
	buildingNumber?: string;
	floor?: string;
	postcode?: string;
	area?: string;
	info?: string;
	city: IRestaurantAddressCity;
	country: IRestaurantCountry;
}

export interface IRestaurantAddressCity {
	name: string;
}

export interface IJoker {
	minimumPromotionDefinitionShiftEnabled: boolean;
}

export interface IPlatformRestaurant {
	restaurantId: string;
	platformId: string;
}

export interface IAuthSchemaRestaurant {
	tokenType: string;
	accessToken: string;
}

export interface IAuthToken {
	tokenType: string;
	accessToken: string;
}

export type OnBackClickFunc = (goBack: () => void) => void;

export interface INewNavigationParameters {
	/** @desc The name for your page (internal) */
	name: string;
	/**
	 * @desc Title shown in the top application bar
	 * @override titleCode
	 * @example null — for using titleCode
	 * */
	title: React.ReactNode;
	/** @desc same as title, but as a translation code */
	titleCode?: string;
	/**
	 * @desc Sets the base path for the routing
	 * @default ''
	 */
	basePath?: string;
	/**
	 * @desc Path segments for the routing
	 * @default []
	 */
	items?: any[];
	/**
	 * @desc Is used to set the selected page
	 * @deprecated
	 * @todo remove
	 */
	defaultPage?: string;
	/**
	 * @desc Changes the menu icon on the top left with a back button functionality
	 * @default false
	 */
	hasBackButton?: boolean;
	/**
	 * @desc Callback to run when clicking back button
	 * @requires hasBackButton
	 * */
	onBackClick?: OnBackClickFunc;
	/**
	 * @desc This property is used when the back button is hit.
	 * @requires hasBackButton
	 * */
	backUrl?: string;
	/**
	 * @desc Can remove possible AppBar plugins on the top-right (like restaurant status)
	 * @default true
	 * @deprecated unused
	 * @todo remove
	 */
	showAppBarPlugins?: boolean;
	/**
	 * @desc Undocumented
	 * @deprecated unused
	 * @todo remove
	 */
	showAppBar?: boolean;
	/**
	 * @desc Defines if the bottom navigation is visible
	 * @default true
	 */
	showBottomNav?: boolean;
	/**
	 * @desc Components that need to appear on bottom right of the app bar
	 * @default null
	 */
	actionButtons?: React.ComponentType;
	/** @desc The component to ensure its permanent visibility at the top of the page */
	stickyContent?: React.ComponentType;
	/**
	 * @desc Makes AppBar take a fixed state, so it always stays at top.
	 * @deprecated unused
	 * @todo remove
	 */
	fixed?: boolean;
	/** @desc Prevents the user from going back by prompting them with a confirmation modal */
	hasUnsavedChanges?: () => Promise<boolean>;
	/**
	 * @desc List of filter components to show in the portal SecondaryBar.
	 * @desc Should not include Outlet selector as this is already provided by the portal.
	 * @link https://components.dev.portal.restaurant/#section-filters
	 */
	filters?: JSX.Element[];
	/** @desc Overrides the default header shrinking behavior based on window scrolling */
	hasShrunkHeader?: boolean;
}

export interface IDateParts {
	day: number;
	month: number;
	year: number;
	hours: number;
	minutes: number;
	seconds: number;
}

export type languageDirection = 'ltr' | 'rtl';

export type UserDataKey =
	| "locale"
	| "name"
	| "email"
	| "phone"
	| "role"
	| "userId"
	| "uniqueUserId"
	| "region"
	| "country"
	| "createdAt";

export type TokenExpiredConditionFn = ({
	res: Response,
	payload: any,
}) => boolean;

export type CreateApiFn = (
	tokenExpiredConditionFn?: TokenExpiredConditionFn
) => Api;

export interface IBatchTokenContent {
	authSchema: Array<[string, string]>;
}

export enum PluginType {
	UNLISTED = "UNLISTED",
	MENU = "MENU",
	SIDEBAR_MENU = "SIDEBAR_MENU",
}

export interface IPluginInfo {
	code: string;
	route: string;
	type: PluginType;
}

export type LoggerContextValue =
	| string
	| number
	| boolean
	| LoggerContext
	| LoggerContext[]
	| undefined;

export interface LoggerContext {
	[x: string]: LoggerContextValue;
}

export type LoggerFn = (message: string, context?: LoggerContext) => void;

export interface Logger {
	debug: LoggerFn;
	info: LoggerFn;
	log: LoggerFn;
	warn: LoggerFn;
	error: LoggerFn;
}

export type VariationExplanation = {
	kind: string;
	from: string;
};

export type VariationTrackInfo = {
	flagEnabled: boolean;
	flagType: string;
	trackerServices: Array<string>;
	variationName: string;
};

export type Variation = {
	variation: boolean;
	abTest: boolean;
	explanation: VariationExplanation;
	relevantContext: string;
	trackInfo?: VariationTrackInfo;
};

export type VariationMap<N extends string = string> = Record<N, Variation>;

export interface IFWFMainConstructor {
	new (fwfParams: any): FWFMain;
}

export interface IPortalSdk {
	createGlobalApi: CreateApiFn;
	createGlobalApiV2: CreateApiFn;
	isUserLoggedIn(): boolean;
	helpCenterResetY(): void;
	helpCenterTranslateY(_y: number): void;
	getUserData(_key: UserDataKey): string;
	getPlatform(): IPlatform;
	getCurrentVendor(): IVendorData;
	getAllVendors(): Promise<IVendorData[]>;
	getOwnedGroups(): Promise<IGroup[]>;
	addVendorObserver(_cb: VendorObserverCallback): ObserverDisposer;
	getSelectedVendors(): IVendorData[];
	addSelectedVendorsObserver(
		_cb: SelectedVendorsObserverCallback
	): ObserverDisposer;
	getLocale(): string;
	getAuth(): IAuthSchemaRestaurant;
	getAuthToken(): IAuthToken;
	subscribeToRefreshStatus(_cb: (isRefreshing: boolean) => void): () => void;
	refreshToken(): Promise<any>;
	t(_key: string, _params?: { [x: string]: string | number }): string;
	i18nextDir(): languageDirection;
	isRTL(): boolean;
	isLTR(): boolean;
	getEnv(): string;
	getRegion(): string;
	setTranslation(_translations: Record<string, string>, _url?: string): void;
	getBaseRoute(): string;
	setNewNavigation(_parameters: INewNavigationParameters): void;
	pushPluginLoadStartedEvent(_options?: { timestamp?: number }): void;
	pushPluginLoadCompletedEvent(_options?: { timestamp?: number }): void;
 	pushPluginLoadFailedEvent(_options?: { timestamp?: number, reason?: Error }): void;
	pushPluginViewEvent(): void;
	pushSectionViewEvent(_options: { sectionId: string }): void;
	/**
	 * @deprecated Please use pushGA4Event insetad.
	 */
	pushGtmEvent(
		_event: string,
		_data?: {},
		_includeDefaultUserData?: boolean
	): void;
	/**
	 * @deprecated Please use pushGA4Event insetad.
	 */
	pushGtmEventV2(
		_event: string,
		_data?: {},
		_includeDefaultUserData?: boolean
	): void;
	pushGA4Event(_event: string, _data?: {}): void;
	applyRestaurantTimezone(_date: Date): Date;
	getRestaurantDateParts(_date: Date): IDateParts;
	getRestaurantDateString(_date: Date): string;
	getRestaurantTimeString(_date: Date): string;
	getRestaurantTimeStringWithSeconds(_date: Date): string;
	withSdk<T>(
		Component: React.ComponentType<T>
	): React.ComponentType<T & { sdk: IPortalSdk }>;
	getFwfClient(): FWFMain;
	getSubject(): Subject;
	getPlugins(): IPluginInfo[];
	getPortalFwfVariation<T extends string>(
		_name: T,
		_fallbackValue: boolean | string,
		_forced: boolean
	): Promise<VariationMap<T>>;
	getPortalFwfVariationValue<T extends string>(
		_name: T,
		_fallBackValue?: boolean | string,
		_forced?: boolean
	): Promise<boolean | string>;
	getPortalFwfVariations<T extends string>(_flags: T[]): Promise<VariationMap>;
	logger: Logger;
	sentry: Pick<Hub, "withScope" | "setTag" | "captureException">;
}

export interface IPluginClass {
	init(): Promise<void> | void;
	getMainComponent(): React.ComponentType<any>;
}

export type RestaurantObserverCallback = (restaurant: IRestaurantData) => void;

export type SelectedRestaurantsObserverCallback = (
	restaurants: IRestaurantData[]
) => void;

export type VendorObserverCallback = (vendor: IVendorData) => void;

export type SelectedVendorsObserverCallback = (vendors: IVendorData[]) => void;

export type ObserverDisposer = () => void;

export function createGlobalApi(
	_tokenExpiredCondition?: TokenExpiredConditionFn
): Api {
	return null;
}

export function createGlobalApiV2(
	_tokenExpiredCondition?: TokenExpiredConditionFn
): Api {
	return null;
}

export function isUserLoggedIn(): boolean {
	return null;
}
export function helpCenterTranslateY(_y: number): void {};

export function helpCenterResetY(): void {};

export function getUserData(_key: UserDataKey): string {
	return "";
}

export function getPlatform(): IPlatform {
	return null;
}

export function getLocale(): string {
	return "";
}

export function getAuth(): IAuthSchemaRestaurant {
	return null;
}

export function getAuthToken(): IAuthSchemaRestaurant {
	return null;
}

export function subscribeToRefreshStatus(
	_cb: (isRefreshing: boolean) => void
): () => void {
	return null;
}

export function refreshToken(): Promise<any> {
	return Promise.resolve(null);
}

export function t(
	_key: string,
	_params?: { [x: string]: string | number }
): string {
	return "";
}


export function i18nextDir(): languageDirection  {
	return null;
}

export function isRTL(): boolean {
	return null;
}

export function isLTR(): boolean {
	return null;
}

export function getRegion(): string {
	return "";
}

export function getEnv(): string {
	return "";
}


export function setTranslation(
	_translations: Record<string, string>,
	_url?: string
): Promise<void> {
	return null;
}

export function getBaseRoute(): string {
	return "";
}

export function setNewNavigation(_parameters: INewNavigationParameters): void {}

/**
 * @deprecated Please use pushGA4Event insetad.
 */
export function pushGtmEvent(
	_event: string,
	_data?: {},
	_includeDefaultUserData?: boolean
): void {}

/**
 * @deprecated Please use pushGA4Event insetad.
 */
export function pushGtmEventV2(
	_event: string,
	_data?: {},
	_includeDefaultUserData?: boolean
): void {}

export function pushGA4Event(_event: string, _data?: {}): void {};

export function pushPluginLoadStartedEvent(_options?: { timestamp?: number }): void {}

export function pushPluginLoadCompletedEvent(_options?: { timestamp?: number }): void {}

export function pushPluginLoadFailedEvent(_options?: { timestamp?: number, reason?: Error }): void {}

export function pushPluginViewEvent(): void {};

export function pushSectionViewEvent(_options: { sectionId: string }): void {};

export function applyRestaurantTimezone(_date: Date): Date {
	return new Date();
}

export function getRestaurantDateParts(_date: Date): IDateParts {
	return {
		year: 0,
		month: 0,
		day: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	};
}

export function getRestaurantDateString(_date: Date): string {
	return "";
}

export function getRestaurantTimeString(_date: Date): string {
	return "";
}

export function getRestaurantTimeStringWithSeconds(_date: Date): string {
	return "";
}

export function getAllVendors(): Promise<IVendorData[]> {
	return Promise.resolve([]);
}

export function getOwnedGroups(): Promise<IGroup[]> {
	return Promise.resolve([]);
}

export function getFwfClient(): FWFMain {
	return null;
}

export function getCurrentVendor(): IVendorData {
	return null;
}

export function addVendorObserver(
	_cb: VendorObserverCallback
): ObserverDisposer {
	return () => {};
}

export function getSelectedVendors(): IVendorData[] {
	return [];
}

export function addSelectedVendorsObserver(
	_cb: SelectedVendorsObserverCallback
): ObserverDisposer {
	return () => {};
}

export function getSubject(): Subject {
	return null;
}

export function getPlugins(): IPluginInfo[] {
	return [];
}

export function getPortalFwfVariation<T extends string>(
	_name: T,
	_fallbackValue: boolean | string,
	_forced: boolean
): Promise<VariationMap<T>> {
	return Promise.resolve(null);
}

export function getPortalFwfVariationValue<T extends string>(
	_name: T,
	_fallBackValue?: boolean | string,
	_forced?: boolean
): Promise<boolean | string> {
	return Promise.resolve(null);
}

export function getPortalFwfVariations<T extends string>(
	_flags: T[]
): Promise<VariationMap> {
	return Promise.resolve(null);
}

export const logger: Logger = {
	debug: () => {},
	info: () => {},
	log: () => {},
	warn: () => {},
	error: () => {},
};

export * from "./withSdk";
